import React, { useEffect, useRef, useState } from 'react';
import Navbar from '../components/Navbar';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './Pricing.css';

function Pricing() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const basicCardRef = useRef(null);
    const [showCouponToast, setShowCouponToast] = useState(true);

    // Read URL params for auto-selection
    const highlightPlan = searchParams.get('plan');   // 'basic' | 'premium'
    const source = searchParams.get('source');         // 'chat' | null

    // Auto-scroll to Basic plan card when redirected from chat
    useEffect(() => {
        if (highlightPlan === 'basic' && basicCardRef.current) {
            // Small delay to ensure DOM is ready
            setTimeout(() => {
                basicCardRef.current.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'center' 
                });
            }, 400);
        }
    }, [highlightPlan]);

    const plans = [
        {
            id: 'basic',
            title: 'Monthly Plan',
            price: '₹6999',
            period: '/mo',
            description: 'Start your spiritual journey with guided wisdom.',
            features: [
                '30 Divine Chats Per Month',
                'Save Conversations (History)',
                'Access to all 700+ Shlokas',
                'Personalized Guidance',
                '24/7 Availability'
            ],
            isPopular: false,
            buttonText: 'Choose Monthly',
            gradient: 'plan-basic'
        },
        {
            id: 'premium',
            title: 'Yearly Plan',
            price: '₹21999',
            period: '/yr',
            description: 'Deepen your connection with priority access and history.',
            features: [
                '360 Divine Conversations for a Year',
                'Everything in Monthly',
                'Prioritized Voice Generation',
                'Save Conversations (History)',
                'Priority Support'
            ],
            isPopular: true,
            buttonText: 'Choose Yearly',
            gradient: 'plan-premium'
        }
    ];

    return (
        <div className="pricing-page">
            <Navbar />
            
            {/* Coupon Floating Notification */}
            {showCouponToast && (
                <div className="coupon-floating-toast">
                    <div className="toast-content">
                        <span className="toast-icon">✨</span>
                        <div className="toast-text">
                            <p>Special Offer: Use <strong>KRISHNA499</strong></p>
                            <span>Get Monthly Plan at just ₹499!</span>
                        </div>
                        <button className="toast-close" onClick={() => setShowCouponToast(false)}>×</button>
                    </div>
                </div>
            )}

            {/* Floating Elements Background */}
            <div className="floating-elements">
                <div className="float-element lotus">🪷</div>
                <div className="float-element om-symbol-2">ॐ</div>
                <div className="float-element diya">🪔</div>
            </div>

            <section className="pricing-section-premium fade-in-section animate-in">
                <div className="container">
                    <div className="section-header-premium pricing-header">
                        <span className="section-badge">Pricing</span>
                        <h2 className="section-title-premium">
                            Choose Your <span className="gradient-text">Journey</span>
                        </h2>
                        <p className="section-subtitle">
                            Invest in your spiritual growth with affordable plans tailored for your needs.
                        </p>
                    </div>

                    {/* ─── Chat-redirect banner ───────────────────────── */}
                    {source === 'chat' && (
                        <div className="pricing-chat-banner" id="pricing-chat-banner">
                            <div className="chat-banner-icon">🔓</div>
                            <div className="chat-banner-text">
                                <strong>Unlock unlimited chats to continue</strong>
                                <span>Choose a plan below to resume your divine conversation</span>
                            </div>
                        </div>
                    )}

                    <div className="pricing-grid-premium">
                        {plans.map((plan) => {
                            const isHighlighted = highlightPlan === plan.id;
                            return (
                                <div 
                                    ref={plan.id === 'basic' ? basicCardRef : null}
                                    className={`pricing-card-premium ${plan.isPopular ? 'popular' : ''} ${plan.gradient} ${isHighlighted ? 'highlighted-plan' : ''}`} 
                                    key={plan.id}
                                    id={`plan-card-${plan.id}`}
                                >
                                    {plan.isPopular && <div className="popular-badge">Most Popular</div>}
                                    {isHighlighted && <div className="recommended-badge">Recommended</div>}

                                    <div className="pricing-card-header">
                                        <h3 className="plan-title">{plan.title}</h3>
                                        <div className="plan-price">
                                            <span className="amount">{plan.price}</span>
                                            <span className="period">{plan.period}</span>
                                        </div>
                                        <p className="plan-desc">{plan.description}</p>
                                    </div>

                                    <div className="pricing-card-body">
                                        <ul className="plan-features">
                                            {plan.features.map((feature, idx) => (
                                                <li key={idx}>
                                                    <span className="feature-check">✓</span>
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <div className="pricing-card-footer">
                                        <button
                                            className={`btn-premium-${plan.isPopular ? 'primary' : 'secondary'} w-100 ${isHighlighted ? 'btn-highlighted' : ''}`}
                                            onClick={() => navigate(`/checkout?plan=${plan.id}`)}
                                        >
                                            {isHighlighted ? '🚀 Get Started Now' : plan.buttonText}
                                        </button>
                                    </div>
                                    <div className="card-glow-effect"></div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="pricing-faq fade-in-section animate-in">
                        <div className="cta-card-premium">
                            <div className="cta-content" style={{ textAlign: 'center', width: '100%' }}>
                                <h2>Have questions about our plans?</h2>
                                <p>We're here to help you choose the right path for your spiritual journey.</p>
                                <button className="btn-premium-secondary btn-large" onClick={() => navigate('/contact')}>
                                    <span className="btn-icon">💬</span>
                                    Contact Support
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default Pricing;
